import * as React from 'react';
import "./gallery.scss";
import {
    AddIcon,
    ArchiveIcon,
    Button,
    CloseIcon,
    FilesImageIcon,
    Flex,
    FlexItem,
    Header,
    Loader,
    MenuButton,
    Text
} from "@fluentui/react-northstar";
import SharepointPicker from "../Utils/SharepointPicker";
import PictureViewer from "./PictureViewer";
import ImagePickerModal from "../Dialogs/ImagePickerModal";
import {ReactComponent as NoImageIcon} from "../../assets/noimage.svg";
import {blobsRootFolderName, retrieveBlobImages, updateBlobImage, uploadBlobFolder} from "../../apis/blobImageApi";
import update from "immutability-helper";
import FolderCreatorDialog from "../Dialogs/FolderCreatorDialog";
import {ImageGallery} from "../../Interfaces/ImageGallery";
import languageObject from "../../translations";
import {AppContext, IAppContextState} from "../../services/AppContext";
import {withRouter, WithRouterProps} from "../../services/TelemetryService/withRouter.hook";

interface IGalleryProps extends WithRouterProps {
}

interface IGalleryState {
    loaded: boolean,
    selectedImage: ImageGallery | undefined,
    showImagePicker: boolean,
    images: Array<ImageGallery>,
    showFolderCreator: boolean,
    newFolderName: string,
    newFolderLocation: string,
    updatingDatabase: boolean,
}

/**
 * Gallery
 */
class Gallery extends React.Component<IGalleryProps, IGalleryState> {
    // Define component context
    static contextType = AppContext;
    context!: IAppContextState;

    /**
     * Constructor
     * @param props
     */
    constructor(props: IGalleryProps) {
        super(props);

        this.state = {
            loaded: false,
            selectedImage: undefined,
            showImagePicker: false,
            images: new Array<ImageGallery>(),
            showFolderCreator: false,
            newFolderName: "",
            newFolderLocation: "",
            updatingDatabase: false,
        }
    }

    /**
     * Component initialization
     */
    async componentDidMount() {
        await this.retrieveImages();
    }

    /**
     * Allow component to render
     * @param nextProps next props
     * @param nextState next state
     */
    shouldComponentUpdate(nextProps: Readonly<IGalleryProps>, nextState: Readonly<IGalleryState>): boolean {
        return this.state.loaded || nextState.loaded;
    }

    /**
     * Component destruction
     */
    componentWillUnmount() {
        this.setState({loaded: false});
    }

    /**
     * Retrieve available images
     */
    retrieveImages = async () => {
        return new Promise<void>((resolve) => {
            retrieveBlobImages().then(images => {
                this.setState({
                    images: images,
                    loaded: true,
                    showImagePicker: false,
                }, resolve);
            })
        })
    }

    /**
     * When an item is selected
     * @param url item url
     */
    handleItemSelection = (url: string) => {
        this.setState({selectedImage: this.state.images?.find(img => img.imageLink === url)});
    }

    /**
     * When an image has been deleted
     */
    onDeleteImage = async (deletedImage: string) => {
        const deletedImageIndex = this.state.images.findIndex(img => img.imageLink === blobsRootFolderName + deletedImage);
        if (deletedImageIndex !== -1) {
            this.setState({
                images: update(this.state.images, {$splice: [[deletedImageIndex, 1]]}),
                selectedImage: undefined
            });
        }
    }

    /**
     * Update images
     * @param images images
     */
    handleImagesUpdate = (images: Array<ImageGallery>): Promise<void> => {
        return new Promise<void>(resolve => {
            this.setState({images: images}, resolve);
        })
    }

    /**
     * When an image has been updated
     */
    onUpdateImage = async (oldImageName: string, newImageName: string) => {
        const oldImageIndex = this.state.images.findIndex(img => img.imageLink === blobsRootFolderName + oldImageName);
        if (oldImageIndex !== -1) {
            const updatedImages = update(this.state.images, {
                [oldImageIndex]: {imageLink: {$set: blobsRootFolderName + newImageName}}
            });
            this.setState({
                images: updatedImages,
                selectedImage: updatedImages[oldImageIndex],
            })
        }
    }

    /**
     * When a folder has been renamed
     * @param oldFolderName old folder name
     * @param newFolderName new folder name
     */
    onRenameFolder = async (oldFolderName: string, newFolderName: string) => {
        const oldSelectedImageIndex = this.state.images.indexOf(this.state.selectedImage ?? {} as ImageGallery);
        const updatedImages = Array<[string, string]>();
        // Update filename of each image
        const images: Array<ImageGallery> = update(this.state.images, {
            $set: this.state.images.map((img): ImageGallery => {
                const newLink = img.imageLink.replace(oldFolderName, newFolderName);
                if (img.imageLink.includes(oldFolderName)) updatedImages.push([img.imageLink, newLink]);
                return {...img, imageLink: newLink}
            })
        })
        this.setState({images, selectedImage: images[oldSelectedImageIndex]});
        for (const img of updatedImages) await updateBlobImage(img[0], img[1]);
    }

    /**
     * When a folder has been deleted
     */
    onDeleteFolder = async (deletedFolder: string) => {
        const deletedFolderIndex = this.state.images.findIndex(f => f.imageLink === deletedFolder);
        if (deletedFolderIndex !== -1) {
            const images = update(this.state.images, {
                $set: this.state.images.filter(img => img.imageLink.indexOf(deletedFolder) === -1)
            });
            const isSelectedImageDeleted = !images.find(i => i.imageLink === this.state.selectedImage?.imageLink);
            this.setState({
                images,
                selectedImage: isSelectedImageDeleted ? undefined : this.state.selectedImage
            });
        }
    }

    /**
     * Create a new folder
     */
    onCreateFolder = (folderPath: string) => {
        return new Promise<void>(async resolve => {
            this.setState({updatingDatabase: true});
            const success = await uploadBlobFolder(folderPath);
            if (success) {
                const newFolder: ImageGallery = {
                    imageLink: folderPath,
                    content: ""
                }
                this.setState({
                    images: [...this.state.images, newFolder],
                    newFolderName: "",
                    updatingDatabase: false
                }, resolve);
            } else {
                console.error("Can't create folder");
                resolve();
            }
        })
    }

    /**
     * Render component
     */
    render() {
        return (
            <Flex column fill gap="gap.medium" className={"p-20px"}>
                {this.renderNavBar()}
                {!this.state.loaded ?
                    <Flex column fill hAlign={"center"} vAlign={"center"}>
                        <Loader/>
                    </Flex>
                    :
                    this.renderImagesBrowser()
                }
                {this.renderDialogs()}
            </Flex>
        );
    }

    /**
     * Render nav bar
     */
    renderNavBar = () => {
        const images = this.state.images.filter(img => img.imageLink.indexOf(".") !== -1);
        let imagesCountLabel = this.state.images.length === 0 ? "" : "(" + images.length + " "
            + languageObject.translate(images.length <= 1 ? "Image" : "Images").toLowerCase() + ")";
        let headerTitle = languageObject.translate("GalleryManagement");
        return (
            <Flex gap={"gap.small"}>
                <Flex gap={"gap.small"} vAlign={"center"}>
                    <FilesImageIcon styles={{color: "#782753", transform: "scale(1.3)"}} size={"large"}/>
                    <Header as={"h2"} styles={{margin: "0"}} content={headerTitle}/>
                    <Text content={imagesCountLabel} size={"large"} weight={"semibold"}/>
                </Flex>
                <FlexItem push>
                    <MenuButton
                        trigger={
                            <Button content={languageObject.translate("Add")} primary icon={<AddIcon outline/>}/>
                        }
                        menu={[
                            {
                                key: "add_image",
                                content: languageObject.translate("Image"),
                                icon: <FilesImageIcon styles={{marginTop: "-5px", marginRight: "5px"}} size={"large"}
                                                      outline/>,
                                onClick: () => this.setState({showImagePicker: true})
                            },
                            {
                                key: "add_folder",
                                content: languageObject.translate("Folder"),
                                icon: <ArchiveIcon outline/>,
                                onClick: () => this.setState({showFolderCreator: true})
                            }
                        ]}
                    />
                </FlexItem>
                <Button icon={<CloseIcon outline/>} content={languageObject.translate("CloseText")}
                        onClick={this.onClose}/>
            </Flex>
        )
    }

    /**
     * Render images browser
     */
    renderImagesBrowser = () => {
        return (
            <Flex fill gap={"gap.medium"} styles={{overflow: "hidden"}}>
                <SharepointPicker
                    fixScrollBarLeft
                    expandFolders
                    showFolderOptions
                    items={this.state.images.map(img => img.imageLink)}
                    handleSelect={this.handleItemSelection}
                    extensions={["jpg", "jpeg", "png"]}
                    selectedFile={this.state.selectedImage}
                    onOpenImagePicker={() => this.setState({showImagePicker: true})}
                    onRenameFolder={this.onRenameFolder}
                    onDeleteFolder={this.onDeleteFolder}
                    noItemsContent={
                        <Flex fill column id={"sharepoint_picker"} hAlign={"center"} vAlign={"center"}>
                            <NoImageIcon width={250}/>
                            <Text size={"largest"} content={languageObject.translate("NoImage")}
                                  styles={{marginTop: "10px", marginBottom: "20px"}}/>
                            <Button secondary content={languageObject.translate("UploadImage")}
                                    icon={<AddIcon outline/>}
                                    onClick={() => this.setState({showImagePicker: true})}/>
                        </Flex>
                    }
                />
                <PictureViewer
                    items={this.state.images.map(img => img.imageLink)}
                    image={this.state.selectedImage}
                    onDelete={this.onDeleteImage}
                    onUpdateImage={this.onUpdateImage}
                    onCreateFolder={this.onCreateFolder}
                    uploadingFolder={this.state.updatingDatabase}
                />
            </Flex>
        )
    }

    /**
     * Render dialogs
     */
    renderDialogs = () => {
        return <>
            <ImagePickerModal
                show={this.state.showImagePicker}
                onClose={() => this.setState({showImagePicker: false})}
                images={this.state.images}
                onImagesUpdated={this.handleImagesUpdate}
            />
            <FolderCreatorDialog
                show={this.state.showFolderCreator}
                images={this.state.images}
                onHide={() => this.setState({showFolderCreator: false})}
                onImagesUpdated={this.handleImagesUpdate}
            />
        </>
    }

    /**
     * On close
     */
    public onClose = () => {
        this.props.navigate("/messages");
    }
}

export default withRouter(Gallery);